import 'dayjs/locale/sv';

import dayjs from 'dayjs';
import Image from 'next/image';

import { Tags, Typography } from '@/components';
import { useMobile } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { PlatformIcon } from '@/utils/platformIcon';
import { prettifyNumber } from '@/utils/prettifyNumber';
import { TranslatedLink } from '@/utils/translatedComponents';

import styles from './Campaign.module.css';

interface CampaignProps {
  campaign: Api.Client.Campaign;
}

export function Campaign({ campaign }: CampaignProps) {
  const isMobile = useMobile();
  const { t } = useTranslation();

  return (
    <TranslatedLink href={`/connect/campaigns/${campaign.campaign_id}`}>
      <div className={styles.campaign}>
        <div className={styles.campaignBrand} data-has-logo={!!campaign.brand_logo_url}>
          {campaign.brand_logo_url && (
            <Image
              className={styles.campaignBrandImage}
              src={campaign.brand_logo_url}
              alt={campaign.brand_name}
              height={50}
              width={50}
            />
          )}

          <div className={styles.campaignBrandName}>
            <Typography sizes={['h6']}>{campaign.brand_name}</Typography>
          </div>
        </div>

        <div className={styles.campaignPlatforms}>
          {!!campaign.platforms.length &&
            campaign.platforms.map((platform) => (
              <span className={styles.campaignPlatformsItem} key={`campaign-platform-${platform}`}>
                {PlatformIcon(platform)}
              </span>
            ))}

          <Typography sizes={['h6']}>{campaign.name}</Typography>
        </div>

        <div className={styles.campaignBudget}>
          {prettifyNumber(campaign.budget?.amount)} {campaign.budget?.currency || '-'}
        </div>

        <div className={styles.campaignDate}>
          <Typography sizes={[isMobile ? 'label' : 'body-small']}>
            <div className={styles.campaignDateValues}>
              <span className={styles.campaignDateValuesStartDate}>
                {`${t('campaign.startDate')}:`}
              </span>

              {campaign.date_start && dayjs(campaign.date_start).isValid()
                ? dayjs(campaign.date_start).format('YYYY-MM-DD')
                : '-'}
            </div>

            <span className={styles.campaignDateValuesEndDate}>
              {campaign.date_end && dayjs(campaign.date_end).isValid()
                ? dayjs(campaign.date_end).format('YYYY-MM-DD')
                : '-'}
            </span>
          </Typography>

          <span className={styles.campaignDateStatus}>
            <Tags
              tags={
                campaign.is_demo
                  ? [
                      {
                        value: t('translatedType.DEMO'),
                        theme: 'DEMO'
                      }
                    ]
                  : [
                      {
                        value: t(`translatedType.${campaign.status}`),
                        theme: campaign.status
                      }
                    ]
              }
            />
          </span>
        </div>
      </div>
    </TranslatedLink>
  );
}
